import { Link, createFileRoute } from '@tanstack/react-router';
import { JOB_ID_PATTERN } from '@pc/lib/asyncOperation';
import { useState, type FormEvent } from 'react';
import { JobStatus } from '../components/jobs/JobStatus';
import { JobsBrowser } from '../components/jobs/JobsBrowser';
import { Card } from '../components/ui';
import { JOB_KINDS, JOB_STATES, type JobFilters } from '@pc/lib/jobs';

/**
 * Same pattern as `DISCORD_ID_PATTERN` in `src/routes/accounts.tsx`: mirrors
 * the contract's own path-parameter pattern for `jobId`
 * (`^[A-Za-z0-9_-]{32}$`), checked client-side so a mistyped or truncated
 * paste surfaces as a plain validation message instead of a round trip that
 * comes back `invalid-job-id`.
 */
// One definition, shared with `useAsyncOperation`, which adopts a `jobId`
// off this same search param and must agree with this route about what
// counts as one.

/**
 * The whole screen's state, and all of it in the URL.
 *
 * `jobId` opens one job; everything else narrows the list. They are in one
 * search type rather than two because they are two halves of the same recovery
 * path — an operator narrows the list until they find the job, opens it, and
 * hands the resulting link to somebody else.
 */
type JobsSearch = JobFilters & { jobId?: string };

/**
 * A search value that is actually there, as the string it was in the URL.
 *
 * A length check and nothing else, the same one `routes/audit.tsx` uses.
 * `src/router.ts` parses every search value as a string, so there is no
 * conversion left to do here and a value arriving as anything else is not a
 * filter.
 *
 * It did convert, once, and the reason is worth keeping: the router's old
 * parser turned `?targetId=120` into the number 120 before any route saw it.
 * `targetId` is very often a VMID, so a bare `typeof value === 'string'` test
 * would have dropped the commonest filter on this screen without a word — and
 * did, on the audit screen next door. Converting back was a patch on this one
 * route; the router now makes the guarantee for all of them.
 */
function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * An enumerated filter is only worth putting in the URL if it is one of the
 * values the API will accept. A hand-edited or stale `?state=finished` is
 * dropped rather than forwarded, which turns a 400 into an unfiltered list —
 * the same tolerance `routes/audit.tsx` shows a URL it did not write.
 */
function stateOf(value: unknown): JobFilters['state'] {
  const raw = str(value);
  return JOB_STATES.find((state) => state === raw);
}

function kindOf(value: unknown): JobFilters['kind'] {
  const raw = str(value);
  return JOB_KINDS.find((kind) => kind === raw);
}

/**
 * The declared filters, and only those, picked out of whatever is in the URL.
 *
 * A route's `validateSearch` does not *strip* the keys it did not claim — the
 * router merges its result over the raw parsed search, which is precisely what
 * lets `useAsyncOperation` round-trip a `jobId` through `/lxc`, a route that
 * never mentions one. The same merge means anything else riding in the address
 * bar reaches this screen too, and a rest-spread of the search would forward it
 * straight into `listJobs` as a filter: `?state=finished` — a plausible typo,
 * and not a state the contract has — would leave the operator looking at a 400
 * where a list should be.
 *
 * So the filters are picked by name rather than by what is left over, and the
 * picking happens in one function used both by `validateSearch` and by the
 * screen, so the two cannot disagree about what a valid filter is.
 */
function readJobFilters(raw: Record<string, unknown>): JobFilters {
  const state = stateOf(raw.state);
  const kind = kindOf(raw.kind);
  const actorOktaId = str(raw.actorOktaId);
  const subjectDiscordID = str(raw.subjectDiscordID);
  const targetId = str(raw.targetId);

  return {
    ...(state !== undefined && { state }),
    ...(kind !== undefined && { kind }),
    ...(actorOktaId !== undefined && { actorOktaId }),
    ...(subjectDiscordID !== undefined && { subjectDiscordID }),
    ...(targetId !== undefined && { targetId }),
  };
}

export const Route = createFileRoute('/jobs')({
  // Held in the URL rather than component state, and for a stronger reason
  // here than on the accounts screen: a create-lxc or delete-account job runs
  // for minutes, well past the lifetime of the tab that submitted it. The job
  // id has to be a link an operator can hand to someone else, bookmark, or
  // reopen later and land back on the same job — not something that is lost
  // the moment the tab closes. The same goes for a filtered list built mid
  // incident. Keys are omitted rather than set to `undefined` because
  // `exactOptionalPropertyTypes` treats those as different things.
  validateSearch: (search: Record<string, unknown>): JobsSearch => {
    const jobId = str(search.jobId);
    return { ...readJobFilters(search), ...(jobId !== undefined && { jobId }) };
  },
  component: JobsScreen,
});

/**
 * A list, with a lookup — in that order.
 *
 * `?jobId=` is the older half of this screen and stays exactly as it was:
 * `useAsyncOperation` writes a job id into the URL the moment an operation is
 * accepted, on whichever screen started it, and that link gets pasted into
 * tickets and handed between shifts. Breaking it would strand every job
 * already referenced somewhere.
 *
 * When one is named, the list gives way to it — the same shape `/accounts` and
 * `/lxc` take, and for the same reason: a browser rendered underneath a single
 * record is a second answer to a question that has already been answered. The
 * way back keeps whatever filters were in force, so following a job out of a
 * narrowed list and returning does not silently widen it.
 */
function JobsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const jobId = search.jobId;
  const filters = readJobFilters(search);
  const isValidId = jobId !== undefined && JOB_ID_PATTERN.test(jobId);

  return (
    <section>
      <h1>Jobs</h1>

      {jobId !== undefined && (
        <p>
          <Link to="/jobs" search={filters}>
            ← All jobs
          </Link>
        </p>
      )}

      {jobId !== undefined && !isValidId && (
        <p role="alert">
          &ldquo;{jobId}&rdquo; is not a valid job id. The contract requires exactly 32 characters of
          letters, digits, underscores or hyphens.
        </p>
      )}

      {isValidId && <JobStatus jobId={jobId} />}

      {jobId === undefined && (
        <>
          {/*
            * Remounted when the committed filters change — a pasted link, the
            * back button, or an apply of its own — so the draft in its form
            * cannot go stale against the URL, and the cursor it has paged to
            * cannot survive into a different result set. Same reasoning as the
            * keyed `SearchFilters` on the audit screen; the alternative is an
            * effect that setStates on every URL change, for a sync the
            * reconciler already does.
            */}
          <JobsBrowser
            key={JSON.stringify(filters)}
            filters={filters}
            onApply={(next) => void navigate({ search: next })}
          />

          {/*
            * Below the list, because the list is the screen now. This is not a
            * filter and must not read as one: `listJobs` has no `jobId`
            * parameter, and this box does not narrow anything — it opens the
            * one job whose id somebody already has.
            */}
          <Card title="Open a job by id">
            <LookupForm
              onLookup={(id) => void navigate({ search: { ...filters, jobId: id } })}
            />
            <p className="empty">
              For an id out of a ticket or a chat thread. If you have lost the id, the list above is
              the way back — it holds every job, in the order they were created.
            </p>
          </Card>
        </>
      )}
    </section>
  );
}

function LookupForm({ onLookup }: { onLookup: (jobId: string) => void }) {
  const [draft, setDraft] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const jobId = draft.trim();
    // Nothing typed is not a lookup. Navigating on an empty box would replace
    // the list with the "not a valid job id" message for a value nobody entered.
    if (jobId.length > 0) onLookup(jobId);
  }

  return (
    <form onSubmit={onSubmit}>
      <label htmlFor="jobId">Job ID</label>{' '}
      <input
        id="jobId"
        name="jobId"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="32-character job id"
        autoComplete="off"
        spellCheck={false}
        size={36}
      />{' '}
      <button type="submit">Look up</button>
    </form>
  );
}
